import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { ModalFront } from '@/shared/ui/Modal/Modal'
import { Button } from '@/shared/ui/buttons/Button/Button'
import { InputField } from '@/shared/ui/inputs/InputField/InputField'
import { VerifyPhoneNumberForm } from '~src/new/features/PhoneNumberVerification'
import { useWidth } from '~src/new/app/providers/WidthProvider'
import { isValidEmailAddress } from '@/shared/lib/emailValidation'
import emailVerificationImage from '~shared/assets/img/email-verification.png'
import emailVerificationSuccessImage from '~shared/assets/img/email-verification-success.png'
import cls from './email-verification-flow.module.scss'

const getEmailRequestErrorMessage = (requestError, labels) => {
  const responseData = requestError?.response?.data
  const responseError = responseData?.error

  if (responseError?.code === 'ERR_MEMBER_EMAIL_ALREADY_EXISTS') {
    return labels.PROFILE_EMAIL_ALREADY_EXISTS
  }
  if (responseError?.code === 'ERR_EMAIL_CHANGE_TOO_FREQUENT') {
    return labels.PROFILE_EMAIL_CHANGE_COOLDOWN
  }

  return responseError?.message || responseData?.alert?.message || labels.PROFILE_EMAIL_REQUEST_ERROR
}

const getMemberPhoneMask = member => {
  return (
    member.mobilePhoneFormated ||
    member.crmContact?.mobilePhone ||
    member.mobilePhone ||
    ''
  )
}

export const EmailVerificationFlow = props => {
  const {
    api,
    member = {},
    isOpen,
    onClose,
    onComplete,
    initialStage = 'prompt',
    dismissible = true,
    contained = false,
    keepLayoutVisible = false,
    layoutOverlayLeft = 80,
    profileEmailChange = false,
    addEmailInput = false,
    labels = {},
  } = props

  const { isMobile } = useWidth()
  const currentEmail = member.emailAddress || member.crmContact?.emailAddress || ''
  const currentEmailNormalized = currentEmail.toLowerCase()
  const confirmedEmailAddress = member.confirmedEmailAddress || member.crmContact?.confirmedEmailAddress
  const currentEmailConfirmed = Boolean(
    currentEmailNormalized &&
      (member.emailConfirmed ||
        confirmedEmailAddress?.toLowerCase() === currentEmailNormalized),
  )
  const authPhoneMask = getMemberPhoneMask(member)
  const [stage, setStage] = useState(initialStage)
  const [emailAddress, setEmailAddress] = useState(currentEmail)
  const [emailCodeRequest, setEmailCodeRequest] = useState(null)
  const [authCodeRequest, setAuthCodeRequest] = useState(null)
  const [successResult, setSuccessResult] = useState(null)
  const [showAddEmailInputLabels, setShowAddEmailInputLabels] = useState(addEmailInput)
  const [authDeliveryMethodsVisible, setAuthDeliveryMethodsVisible] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const hasCurrentEmail = Boolean(currentEmail)
  const useMobileBottomSheet =
    isMobile && ['prompt', 'auth', 'input', 'email-code', 'success'].includes(stage)
  const showMobileSheetControls = useMobileBottomSheet && !authDeliveryMethodsVisible
  const useLayoutVisibleOverlay = keepLayoutVisible && !isMobile
  const useFullScreenEmailOverlay = !contained && !useLayoutVisibleOverlay
  const modalContainerStyle = {
    zIndex: 10001,
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    ...(useLayoutVisibleOverlay
      ? {
          pointerEvents: 'none',
          '--email-verification-layout-left': `${layoutOverlayLeft}px`,
        }
      : {}),
  }

  const close = useCallback(async () => {
    if (!dismissible) return
    onClose?.()
    try {
      await api.dismissEmailVerificationPrompt?.().request
    } catch (requestError) {
      // The prompt is intentionally non-blocking; closing it should remain available offline.
    }
  }, [api, dismissible, onClose])

  const showErrorAlert = useCallback(
    (requestError, message) => {
      const alertMessage = requestError?.response?.data?.alert?.message

      if (!alertMessage && message) {
        api.connector?.dispatchAlert?.({
          message,
          variant: 'error',
        })
      }
    },
    [api],
  )

  const requestEmailCode = useCallback(
    async nextEmail => {
      setIsSubmitting(true)
      setError('')
      try {
        const nextEmailNormalized = nextEmail.toLowerCase()
        const id = hasCurrentEmail && nextEmailNormalized === currentEmailNormalized ? 'email-confirmation' : 'email-change'
        const { request } = api.requestEmailVerificationCode(id, nextEmail)
        const result = await request
        setEmailAddress(nextEmail)
        setEmailCodeRequest({ ...result, id })
        setStage('email-code')
        return result
      } catch (requestError) {
        const message = getEmailRequestErrorMessage(requestError, labels)

        setError(message)
        showErrorAlert(requestError, message)
        return false
      } finally {
        setIsSubmitting(false)
      }
    },
    [api, currentEmailNormalized, hasCurrentEmail, labels, showErrorAlert],
  )

  const handleEmailSubmit = useCallback(
    event => {
      event.preventDefault()
      const nextEmail = emailAddress

      if (!isValidEmailAddress(nextEmail)) {
        setError(labels.PROFILE_EMAIL_ERROR_TEXT)
        return
      }
      if (
        profileEmailChange &&
        currentEmailConfirmed &&
        nextEmail.toLowerCase() === currentEmailNormalized
      ) {
        setError(labels.PROFILE_EMAIL_ALREADY_CONFIRMED)
        showErrorAlert(null, labels.PROFILE_EMAIL_ALREADY_CONFIRMED)
        return
      }

      requestEmailCode(nextEmail)
    },
    [
      currentEmailConfirmed,
      currentEmailNormalized,
      emailAddress,
      labels,
      profileEmailChange,
      requestEmailCode,
      showErrorAlert,
    ],
  )

  const handleAuthRequest = useCallback(sendType => {
    const result = api.requestCode('email-change-authorization', sendType)

    return {
      ...result,
      request: result.request.then(response => {
        setAuthCodeRequest(response)
        return response
      }),
    }
  }, [api])

  const handleAuthVerify = useCallback(async verificationCode => {
    const { request } = api.verifyCode('email-change-authorization', verificationCode)
    return request
  }, [api])

  const handleAuthSuccess = useCallback(() => {
    setError('')
    setEmailAddress('')
    setAuthDeliveryMethodsVisible(false)
    setShowAddEmailInputLabels(false)
    setStage('input')
  }, [])

  const handleEmailCodeRequest = useCallback(() => {
    return api.requestEmailVerificationCode(emailCodeRequest.id, emailAddress)
  }, [api, emailAddress, emailCodeRequest])

  const handleEmailCodeVerify = useCallback(async verificationCode => {
    const { request } = api.verifyEmailVerificationCode(emailCodeRequest.id, verificationCode)
    return request
  }, [api, emailCodeRequest])

  const handleComplete = useCallback(result => {
    setSuccessResult(result)
    setStage('success')
  }, [])

  const handleReturnToWork = useCallback(() => {
    onComplete?.(successResult)
    onClose?.()
  }, [onClose, onComplete, successResult])

  const renderContent = () => {
    if (stage === 'prompt') {
      return (
        <div className={classNames(cls.content, cls.prompt)}>
          <div className={cls.promptMain}>
            <img className={cls.promptImage} src={emailVerificationImage} alt="" />
            <div className={cls.promptText}>
              <h2 className={cls.title}>
                {hasCurrentEmail ? labels.PROFILE_CONFIRM_EMAIL : labels.PROFILE_ADD_EMAIL}
              </h2>
              <p className={cls.description}>
                {hasCurrentEmail ? (
                  <>
                    {labels.PROFILE_CONFIRM_EMAIL_DESCRIPTION}{' '}
                    <strong>{currentEmail}</strong>
                  </>
                ) : (
                  labels.PROFILE_ADD_EMAIL_DESCRIPTION
                )}
              </p>
            </div>
          </div>
          <div className={cls.actions}>
            <Button
              className={cls.promptButton}
              onClick={() => (hasCurrentEmail ? requestEmailCode(currentEmail) : setStage('input'))}
            >
              {hasCurrentEmail ? labels.PROFILE_CONFIRM : labels.PROFILE_ADD_EMAIL_BUTTON}
            </Button>
            {hasCurrentEmail ? (
              <Button
                variant="OUTLINE"
                className={`${cls.promptButton} ${cls.secondaryButton}`}
                onClick={() => setStage('auth')}
              >
                {labels.PROFILE_CHANGE_EMAIL}
              </Button>
            ) : null}
          </div>
          {error ? <p className={cls.error}>{error}</p> : null}
        </div>
      )
    }

    if (stage === 'auth') {
      return (
        <VerifyPhoneNumberForm
          labels={{
            ...labels,
            codeTitle: labels.PROFILE_CONFIRM_EMAIL_CHANGE,
            codeProblems: '',
            codeResend: labels.PROFILE_EMAIL_CODE_RESEND,
          }}
          variant="emailPopup"
          voiceCallFirst
          initialPhoneMask={authPhoneMask}
          onRequestCode={handleAuthRequest}
          requestCodeOnMount
          onVerifyCode={handleAuthVerify}
          onVerifySuccess={handleAuthSuccess}
          delayBeforeCodeResend={authCodeRequest?.delay}
          resendDeliveryType="sms"
          onDeliveryMethodsVisibleChange={setAuthDeliveryMethodsVisible}
        />
      )
    }

    if (stage === 'input') {
      return (
        <form
          className={classNames(cls.content, cls.inputContent)}
          onSubmit={handleEmailSubmit}
          noValidate
        >
          <div className={cls.inputHeader}>
            <h2 className={cls.title}>
              {showAddEmailInputLabels
                ? labels.PROFILE_ADD_EMAIL_INPUT_TITLE
                : labels.PROFILE_INPUT_EMAIL_TITLE}
            </h2>
            <p className={cls.description}>
              {showAddEmailInputLabels
                ? labels.PROFILE_ADD_EMAIL_INPUT_DESCRIPTION
                : labels.PROFILE_INPUT_EMAIL_DESCRIPTION}
            </p>
          </div>
          <InputField
            containerClassName={classNames(cls.emailInputContainer, {
              [cls.emailInputContainerWithError]: error,
            })}
            className={cls.emailInput}
            type="text"
            inputMode="email"
            placeholder={labels.PROFILE_EMAIL_PLACEHOLDER}
            value={emailAddress}
            onChange={event => {
              setEmailAddress(event.target.value)
              setError('')
            }}
            onBlur={() => {
              if (emailAddress && !isValidEmailAddress(emailAddress)) {
                setError(labels.PROFILE_EMAIL_ERROR_TEXT)
              }
            }}
            autoFocus
            autoComplete="email"
            errorLabel={error}
          />
          <div className={classNames(cls.actions, cls.inputActions)}>
            <Button
              type="submit"
              className={classNames(cls.promptButton, cls.inputButton)}
              disabled={isSubmitting}
            >
              {labels.PROFILE_SEND}
            </Button>
            <Button
              type="button"
              variant="OUTLINE"
              className={classNames(cls.promptButton, cls.inputButton, cls.secondaryButton)}
              onClick={() => (profileEmailChange || !hasCurrentEmail ? close() : setStage('prompt'))}
            >
              {labels.PROFILE_CANCEL}
            </Button>
          </div>
        </form>
      )
    }

    if (stage === 'success') {
      return (
        <div className={classNames(cls.content, cls.successContent)}>
          <div className={cls.successMain}>
            <img className={cls.successImage} src={emailVerificationSuccessImage} alt="" />
            <div className={cls.successText}>
              <h2 className={cls.title}>{labels.PROFILE_EMAIL_SUCCESS_TITLE}</h2>
              <p className={cls.description}>
                {labels.PROFILE_EMAIL_SUCCESS_DESCRIPTION}
              </p>
            </div>
          </div>
          <Button
            className={classNames(cls.promptButton, cls.successButton)}
            onClick={handleReturnToWork}
          >
            {profileEmailChange ? labels.PROFILE_TO_PROFILE : labels.PROFILE_BACK_TO_WORK}
          </Button>
        </div>
      )
    }

    return (
      <VerifyPhoneNumberForm
        labels={{
          ...labels,
          codeTitle: labels.PROFILE_CONFIRM_EMAIL,
          codeProblems: '',
          codeResend: labels.PROFILE_EMAIL_CODE_RESEND,
        }}
        variant="emailPopup"
        leadingImageSrc={emailVerificationImage}
        renderDescription={recipientMask => (
          <>
            {labels.PROFILE_EMAIL_CODE_DESCRIPTION}{' '}
            <strong className={cls.recipientEmail}>{recipientMask || emailAddress}</strong>
          </>
        )}
        initialRecipientMask={emailCodeRequest?.recipientMask}
        initialDeliveryType="email"
        initialAvailableDeliveryTypes={emailCodeRequest?.availableDeliveryTypes}
        initialCanChooseAnotherMethod={emailCodeRequest?.canChooseAnotherMethod}
        delayBeforeCodeResend={emailCodeRequest?.delay}
        onRequestCode={handleEmailCodeRequest}
        onVerifyCode={handleEmailCodeVerify}
        onVerifySuccess={handleComplete}
        secondaryActionLabel={labels.PROFILE_CHANGE_EMAIL}
        onSecondaryAction={() => {
          setShowAddEmailInputLabels(false)
          setStage('input')
        }}
      />
    )
  }

  if (!isOpen) {
    return null
  }

  return (
    <ModalFront
      isOpen={isOpen}
      onClose={close}
      withBackButton={false}
      onBackClick={() =>
        setStage(
          stage === 'email-code' && emailCodeRequest?.id !== 'email-confirmation'
            ? 'input'
            : 'prompt',
        )
      }
      className={classNames(cls.modal, {
        [cls.promptModal]: stage === 'prompt',
        [cls.authModal]: stage === 'auth',
        [cls.inputModal]: stage === 'input',
        [cls.profileAuthModal]: profileEmailChange && stage === 'auth',
        [cls.profileAuthDeliveryModal]:
          profileEmailChange && stage === 'auth' && authDeliveryMethodsVisible,
        [cls.emailCodeModal]: stage === 'email-code',
        [cls.successModal]: stage === 'success',
      })}
      classNameBody={cls.body}
      classNameOverlay={classNames({
        [cls.emailOverlay]: useFullScreenEmailOverlay,
        [cls.containedOverlay]: contained,
        [cls.mobileSheetOverlay]: useMobileBottomSheet,
        [cls.layoutVisibleOverlay]: useLayoutVisibleOverlay,
      })}
      contained={contained}
      noPortal={contained}
      hideCross={!dismissible || useMobileBottomSheet || stage === 'success'}
      disableMobileSheet={contained || useMobileBottomSheet}
      containerProps={{ style: modalContainerStyle }}
    >
      {showMobileSheetControls ? <div className={cls.mobileSheetHandle} /> : null}
      {showMobileSheetControls ? (
        <button
          type="button"
          className={cls.sheetClose}
          onClick={stage === 'success' ? handleReturnToWork : close}
          aria-label={labels.PROFILE_CLOSE}
        >
          ×
        </button>
      ) : null}
      {renderContent()}
    </ModalFront>
  )
}

EmailVerificationFlow.propTypes = {
  api: PropTypes.shape({
    connector: PropTypes.shape({
      dispatchAlert: PropTypes.func,
    }),
    dismissEmailVerificationPrompt: PropTypes.func,
    requestCode: PropTypes.func,
    verifyCode: PropTypes.func,
    requestEmailVerificationCode: PropTypes.func,
    verifyEmailVerificationCode: PropTypes.func,
  }).isRequired,
  member: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onComplete: PropTypes.func,
  initialStage: PropTypes.oneOf(['prompt', 'auth', 'input', 'success']),
  dismissible: PropTypes.bool,
  contained: PropTypes.bool,
  keepLayoutVisible: PropTypes.bool,
  layoutOverlayLeft: PropTypes.number,
  profileEmailChange: PropTypes.bool,
  addEmailInput: PropTypes.bool,
}
